import { convertCommasToNumber } from "@/utils/input";

/**
 * Adds zero padding to a number or string to reach the desired length.
 *
 * @param {number | string} num - The number or string to be padded.
 * @param {number} length - The desired length of the output string.
 * @returns {string} - The zero-padded number or string.
 */
export const zeroPad = (num: number | string, length: number): string => {
  const numStr = num.toString();
  const zerosNeeded = length - numStr.length;
  const zeros = "0".repeat(zerosNeeded);

  return zeros + numStr;
};

/**
 * Removes leading zeros from a string.
 *
 * @param {string} value - The string from which to remove leading zeros.
 * @returns {string} - The string without leading zeros.
 */
export const removeLeadingZero = (value: string): string => {
  return value.replace(/^0+/, "");
};

/**
 * Checks if a value is a number.
 *
 * @param {unknown} value - The value to check.
 * @returns {value is number} - True if the value is a number, false otherwise.
 */
export const isNumber = (value: unknown): value is number => {
  return typeof value === "number" && !isNaN(value);
};

/**
 * Converts a value to a number. If the value is not a number, returns 0.
 *
 * @param {unknown} value - The value to convert.
 * @returns {number} - The converted number, or 0 if the value is not a number.
 */
export const toNumber = (value: unknown): number => {
  if (typeof value === "string") {
    // Remove commas from the string before converting
    value = convertCommasToNumber(value);
  }
  const num = Number(value);
  return isNaN(num) || !value ? 0 : num;
};

/**
 * Floors a number to two decimal places.
 *
 * @param {number} value - The number to floor.
 * @returns {number} - The floored number with two decimal places.
 */
export const floorToTwoDecimals = (value: number): number => {
  if (!isNumber(value)) return 0;

  return Number(value?.toFixed(2));
};

export const getCircledNumber = (num: number): string => {
  // ①〜⑳
  if (num >= 1 && num <= 20) {
    return String.fromCharCode(0x2460 + num - 1);
  }

  // ㉑〜㉟ (21〜37)
  if (num >= 21 && num <= 37) {
    return String.fromCharCode(0x3250 + num - 20);
  }

  // fallback
  return `(${num})`;
};

export const parseNumberWithCommas = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  const parsed = parseInt(String(value).replace(/,/g, ""), 10);
  return isNaN(parsed) ? null : parsed;
};
